import React from 'react';
import { CommissionTier } from '../../../types/commissionTier';
import { PropertyCommissionRow } from './PropertyCommissionRow';
import { PlusIcon, CalculatorIcon } from '../../Icons';
import {
  normalizeCommissionTiers,
  generateDefaultCommissionTiers,
  calculateTierCommission,
} from '../../../utils/commissionTierUtils';

interface Props {
  value?: CommissionTier[] | null;
  precioBase?: number;
  rateBase?: number;
  onChange: (tiers: CommissionTier[]) => void;
}

export const PropertyCommissionTable: React.FC<Props> = ({
  value,
  precioBase = 0,
  rateBase = 0,
  onChange,
}) => {
  const tiers = normalizeCommissionTiers(value);

  const handleAddRow = () => {
    const defaultPct = rateBase > 0 && rateBase <= 0.25 ? Number((rateBase * 100).toFixed(2)) : (rateBase || 3);
    const newTier: CommissionTier = {
      id: `tier-${Date.now()}`,
      porcentaje: defaultPct,
      precio: precioBase || 0,
      valor_comision: calculateTierCommission(precioBase || 0, defaultPct),
      descripcion: '',
    };
    onChange([...tiers, newTier]);
  };

  const handleUpdateRow = (index: number, updated: Partial<CommissionTier>) => {
    const next = [...tiers];
    next[index] = { ...next[index], ...updated };
    onChange(next);
  };

  const handleRemoveRow = (index: number) => {
    onChange(tiers.filter((_, i) => i !== index));
  };

  const handleGenerateDefaults = () => {
    const defaults = generateDefaultCommissionTiers(precioBase, rateBase);
    onChange(defaults);
  };

  return (
    <div className="p-3.5 bg-slate-50/90 rounded-2xl border border-slate-200/80 space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <h5 className="font-black text-slate-800 uppercase tracking-wider text-xs flex items-center gap-1.5">
            📊 Desglose de Comisión por Precio de Venta
          </h5>
          <p className="text-[11px] text-slate-500">
            Define el porcentaje y valor de comisión según el precio final de venta.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {precioBase > 0 && tiers.length === 0 && (
            <button
              type="button"
              onClick={handleGenerateDefaults}
              className="px-2.5 py-1 bg-amber-100 hover:bg-amber-200 text-amber-900 rounded-lg text-[11px] font-bold flex items-center gap-1 transition-all"
            >
              <CalculatorIcon className="w-3.5 h-3.5" />
              Sugerir Tramos
            </button>
          )}
          <button
            type="button"
            onClick={handleAddRow}
            className="px-2.5 py-1 bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-[11px] font-bold flex items-center gap-1 shadow-sm transition-all"
          >
            <PlusIcon className="w-3.5 h-3.5" />
            Agregar Fila
          </button>
        </div>
      </div>

      {tiers.length === 0 ? (
        <div className="text-center py-5 px-3 border border-dashed border-slate-200 rounded-xl bg-white/70">
          <p className="text-xs text-slate-500 font-medium mb-2">
            No hay tramos de comisión configurados para este inmueble.
          </p>
          <div className="flex justify-center gap-2">
            <button
              type="button"
              onClick={handleAddRow}
              className="text-xs text-sky-700 font-bold hover:underline"
            >
              + Agregar primera fila
            </button>
            {precioBase > 0 && (
              <>
                <span className="text-slate-300">•</span>
                <button
                  type="button"
                  onClick={handleGenerateDefaults}
                  className="text-xs text-amber-700 font-bold hover:underline"
                >
                  Generar sugerencias automáticas
                </button>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-xl bg-white shadow-2xs">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-100/80 border-b border-slate-200 text-[10px] uppercase font-black tracking-wider text-slate-600">
                <th className="py-2 px-3 w-28">Porcentaje (%)</th>
                <th className="py-2 px-3">Precio Venta ($ COP)</th>
                <th className="py-2 px-3 w-36">Comisión Resultante</th>
                <th className="py-2 px-3">Escenario / Etiqueta</th>
                <th className="py-2 px-2 text-center w-10"></th>
              </tr>
            </thead>
            <tbody>
              {tiers.map((tier, idx) => (
                <PropertyCommissionRow
                  key={tier.id || idx}
                  tier={tier}
                  index={idx}
                  onUpdate={handleUpdateRow}
                  onRemove={handleRemoveRow}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
